import React, { useImperativeHandle, useMemo } from "react";

import { FASelectInput } from "@impero-com/common/components/form/formAtomFields/faSelectInput";
import { formAtom } from "@impero-com/common/utils/form/formAtom";
import { useCompoundFormState } from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { FieldError, FormLabel } from "@impero-com/ui";
import { useAtomValue } from "jotai";
import { focusAtom } from "jotai-optics";

import { CurrencyCode } from "api/generated/model";
import { CompoundFormForwardedRef } from "components/fieldBuilder/fieldBuilder";

import { useOrganizationCurrenciesQuery } from "../../queries/useOrganizationCurrenciesQuery";

import {
    getDefaultCurrencyOptions,
    getDefaultCurrencySchema,
    MoneyInputFieldType,
} from "./fieldBuilderUtils";

interface MoneyInputEditorProps {
    atom: FormAtom<MoneyInputFieldType>;
    disabled?: boolean;
}

const MoneyInputEditorInner = (
    { atom: fieldTypeAtom, disabled = false }: MoneyInputEditorProps,
    ref: CompoundFormForwardedRef
) => {
    const { t, i18n } = useTranslation();
    const defaultCurrencyAtom: FormAtom<CurrencyCode> = useMemo(
        () =>
            formAtom<CurrencyCode>(
                focusAtom(fieldTypeAtom, (fieldType) =>
                    fieldType.prop("defaultCurrency")
                ),
                getDefaultCurrencySchema(),
                fieldTypeAtom
            ),
        [fieldTypeAtom]
    );

    const allAtoms = useMemo(
        () => ({ defaultCurrencyAtom }),
        [defaultCurrencyAtom]
    );
    const { validate: validateForm } = useCompoundFormState(
        allAtoms,
        defaultCurrencyAtom
    );
    useImperativeHandle(
        ref,
        () => ({
            validate(force) {
                return validateForm(force);
            },
        }),
        [validateForm]
    );

    const {
        data: currencies,
        isPending,
        isError,
    } = useOrganizationCurrenciesQuery();
    const selectedCurrency = useAtomValue(defaultCurrencyAtom);

    const options = useMemo(
        () =>
            getDefaultCurrencyOptions(
                currencies ?? [],
                selectedCurrency,
                i18n.language
            ),
        [currencies, selectedCurrency, i18n.language]
    );

    return (
        <>
            <FormLabel>
                {t("FieldBuilder_Drawer_Money_DefaultCurrency")}
            </FormLabel>
            <FASelectInput
                atom={defaultCurrencyAtom}
                options={options}
                disabled={disabled || isPending || isError}
            />
            {isError && (
                <FieldError
                    error={t("FieldBuilder_Drawer_Money_Currencies_LoadError")}
                />
            )}
        </>
    );
};

export const MoneyInputEditor = React.forwardRef(MoneyInputEditorInner);
